import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Menu, X, Code2, Sun, Moon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/utils';
import { useTheme } from '../../hooks/useTheme';
import ThemeToggle from '../ThemeToggle';

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'Services', href: '/services' },
  { name: 'Portfolio', href: '/portfolio' },
  { name: 'Case Studies', href: '/case-studies' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'Blog', href: '/blog' },
  { name: 'About', href: '/about' },
  { name: 'Contact', href: '/contact' },
];

interface MobileMenuProps {
  navigation: { name: string; href: string }[];
  location: { pathname: string };
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  onClose: () => void;
}

function MobileMenu({ navigation, location, theme: resolvedTheme, toggleTheme, onClose }: MobileMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Focus trap: keep Tab/Shift+Tab within the menu
  useEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;

    const focusableSelector = 'a[href], button, [tabindex]:not([tabindex="-1"])';
    const focusables = () => menu.querySelectorAll<HTMLElement>(focusableSelector);

    // Focus first item on mount
    const items = focusables();
    if (items.length > 0) items[0].focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const items = focusables();
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <>
      {/* Backdrop overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 top-14 z-40 bg-background/60 backdrop-blur-sm md:hidden"
        onClick={onClose}
      />
      {/* Slide-in menu */}
      <motion.div
        ref={menuRef}
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed right-0 top-14 bottom-0 z-50 w-72 border-l bg-background md:hidden overflow-y-auto"
        role="menu"
        aria-label="Mobile navigation"
      >
        <div className="px-6 py-6 space-y-4">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={onClose}
              className={cn(
                'block text-sm font-medium transition-colors hover:text-primary py-2',
                location.pathname === item.href
                  ? 'text-primary'
                  : 'text-muted-foreground',
              )}
              role="menuitem"
              aria-current={location.pathname === item.href ? 'page' : undefined}
            >
              {item.name}
            </Link>
          ))}
          <div className="border-t pt-4 mt-4">
            <button
              onClick={toggleTheme}
              className="flex w-full items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors py-2"
              role="menuitem"
            >
              {resolvedTheme === 'light' ? <Moon className="h-4 w-4" aria-hidden="true" /> : <Sun className="h-4 w-4" aria-hidden="true" />}
              {resolvedTheme === 'light' ? 'Dark Mode' : 'Light Mode'}
            </button>
            <Link
              to="/contact"
              onClick={onClose}
              className="mt-4 block w-full rounded-md bg-primary px-4 py-2 text-center text-sm font-medium text-primary-foreground hover:bg-primary/90"
              role="menuitem"
            >
              Get Started
            </Link>
          </div>
        </div>
      </motion.div>
    </>
  );
}

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { theme, toggle: toggleTheme } = useTheme();
  // theme is the resolved value ('light' | 'dark')

  // Item 11: Sticky header scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Close mobile menu on route change
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Restore focus to menu button when closing
  const handleMenuClose = useCallback(() => {
    setMobileMenuOpen(false);
    // Defer focus restoration to after AnimatePresence exit
    requestAnimationFrame(() => menuButtonRef.current?.focus());
  }, []);

  return (
    <>
      {/* Skip to content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:outline-none"
      >
        Skip to content
      </a>
      <header
        className={cn(
          'sticky top-0 z-50 w-full border-b transition-all duration-300',
          scrolled
            ? 'bg-background/80 backdrop-blur-xl shadow-sm'
            : 'bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60',
        )}
      >
        <nav
          className={cn(
            'container mx-auto flex items-center justify-between px-4 transition-all duration-300',
            scrolled ? 'h-14' : 'h-16',
          )}
          aria-label="Main navigation"
        >
          <Link to="/" className="flex items-center gap-2" aria-label="Innovation Coders - Home">
            <Code2 className="h-8 w-8 text-primary" aria-hidden="true" />
            <span className="text-xl font-bold">Innovation Coders</span>
          </Link>

          {/* Desktop navigation — Item 12: Active nav indicator */}
          <div className="hidden md:flex md:gap-8">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'relative text-sm font-medium transition-colors hover:text-primary py-1',
                    isActive ? 'text-primary' : 'text-muted-foreground',
                  )}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {item.name}
                  {isActive && (
                    <motion.div
                      layoutId="activeNav"
                      className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full"
                      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    />
                  )}
                </Link>
              );
            })}
          </div>

          <div className="hidden md:flex md:items-center md:gap-4">
            <ThemeToggle />
            <Link
              to="/contact"
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            ref={menuButtonRef}
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" aria-hidden="true" />
            ) : (
              <Menu className="h-6 w-6" aria-hidden="true" />
            )}
          </button>
        </nav>

        {/* Item 13: Mobile navigation with slide animation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <MobileMenu
              navigation={navigation}
              location={location}
              theme={theme}
              toggleTheme={toggleTheme}
              onClose={handleMenuClose}
            />
          )}
        </AnimatePresence>
      </header>
    </>
  );
}
